import type { UseSuspenseQueryResult } from "@tanstack/react-query";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { CustomerBankingSchemas, BaseSchemas } from "api-types";
import { useAxios } from "auth-sdk";
import { useCustomerAppConfig } from "undercarriage";

type BankingMembershipConfigResponse = CustomerBankingSchemas.BankingMembershipConfigResponse;

export const CUSTOMER_BANKING_MEMBERSHIP_CONFIG_QUERY_KEY = "customer-banking-membership-config";
const CUSTOMER_BANKING_MEMBERSHIP_CONFIG_ENDPOINT = "/api/:customerSlug/banking/membership-config";

// Company-less users have no `customerDomain`. This endpoint is company-scoped, so we treat
// "no company" as "not a banking customer" without hitting the network (see queryFn below).
const NO_COMPANY_MEMBERSHIP_CONFIG: BankingMembershipConfigResponse = {
  show_application: false,
  has_completed_mfa_setup: false,
  is_approved_banking_customer: false,
};

export const useGetBankingMembershipConfigQuery = (): UseSuspenseQueryResult<BankingMembershipConfigResponse> => {
  const client = useAxios();
  const { data: appConfig } = useCustomerAppConfig();

  const requestPath = CUSTOMER_BANKING_MEMBERSHIP_CONFIG_ENDPOINT.replace(":customerSlug", appConfig.customerDomain);

  return useSuspenseQuery({
    queryKey: [CUSTOMER_BANKING_MEMBERSHIP_CONFIG_QUERY_KEY, appConfig.analyticsAttributes?.isStaff],
    // Skip the request for staff users, or users with no company.
    queryFn: async () => {
      if (!appConfig.customerDomain || appConfig.analyticsAttributes?.isStaff) {
        return NO_COMPANY_MEMBERSHIP_CONFIG;
      }
      const response = await client.get<BankingMembershipConfigResponse>(requestPath);
      return response.data;
    },
  });
};

export const useIsBankingCustomer = (): {
  isApprovedBankingCustomer: boolean;
  isBankingCustomer: boolean;
  hasCompletedMfaSetup: boolean;
} => {
  const { data } = useGetBankingMembershipConfigQuery();
  return {
    isApprovedBankingCustomer: data.is_approved_banking_customer ?? false,
    isBankingCustomer: data.show_application ?? false,
    hasCompletedMfaSetup: data.has_completed_mfa_setup ?? false,
  };
};

export const useHasPermissions = (permissions: BaseSchemas.CompanyPermission[]): boolean => {
  const {
    data: { userPermissions },
  } = useCustomerAppConfig();
  return permissions.every((permission) => userPermissions.includes(permission));
};
